import React, { useEffect, useMemo, useState } from 'react';
import './PopupQuiz.css';

export default function PopupQuiz({ onClose, inline = false }) {
  const totalSteps = 6;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    propertyType: '',
    bedrooms: '',
    budget: '',
    facility: [],
    purpose: '',
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+91'
  });

  useEffect(() => {
    if (!inline) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
    return () => {};
  }, [inline]);

  const progress = useMemo(() => Math.round(((step - 1) / totalSteps) * 100), [step]);

  const next = () => { if (step < totalSteps) setStep(step + 1); };
  const back = () => { if (step > 1) setStep(step - 1); };
  const select = (key, value) => { setData({ ...data, [key]: value }); };

  const toggleFacility = (value) => {
    const current = Array.isArray(data.facility) ? data.facility : [];
    const exists = current.includes(value);
    const nextFacilities = exists ? current.filter(f => f !== value) : [...current, value];
    setData({ ...data, facility: nextFacilities });
  };

  const canNext = () => {
    if (step === 1) return !!data.propertyType;
    if (step === 2) return !!data.bedrooms;
    if (step === 3) return !!data.budget;
    if (step === 4) return Array.isArray(data.facility) && data.facility.length > 0;
    if (step === 5) return !!data.purpose;
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbw7-Xswneoi4uaCpiOugIQlw-JTbWEgtEQTHAUw4wmg0wGfFd0VFD65KltxzlEIG2gAnA/exec';

      const params = new URLSearchParams({
        propertyType: data.propertyType,
        bedrooms: data.bedrooms,
        budget: data.budget,
        facility: Array.isArray(data.facility) ? data.facility.join(', ') : '',
        purpose: data.purpose,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        countryCode: data.countryCode,
      });

      const res = await fetch(`${SCRIPT_URL}?${params.toString()}`, {
        method: 'GET',
        redirect: 'follow',
      });
      const result = await res.json();

      if (result.success) {
        setStep(7);
      } else {
        alert('Something went wrong while sending. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting quiz:', err);
      alert('There was an error submitting your response. Please email us directly.');
    } finally {
      setLoading(false);
    }
  };

  const cutoutOptions = [
    { src: '/cut outs/villa 1.png', label: 'Villa', value: 'villa' },
    { src: '/cut outs/Glamping-dome-6m-size-4.png', label: 'Glamping Dome', value: 'glamping-dome' },
    { src: '/cut outs/Wholesale-40FT-Modern-Prefabricated-House-Space-Capsule-House-Mobile-Cabin-for-Resort.png', label: 'Space Capsule', value: 'space-capsule' },
    { src: '/cut outs/delimitation-land-plots.png', label: 'Land Plots', value: 'land-plots' },
  ];

  if (inline) {
    return (
      <div className="popup-quiz inline">
        {step !== 7 && (
          <div className="pq-header">
            <button className="pq-close" onClick={onClose}>✕</button>
            <h2>
              {step === 1 && 'What type of property are you looking for?'}
              {step === 2 && 'How many bedrooms do you need?'}
              {step === 3 && "What's your budget range?"}
              {step === 4 && 'Which facility do you prefer most?'}
              {step === 5 && "What's your investment purpose?"}
              {step === 6 && 'How can we reach you?'}
            </h2>
          </div>
        )}

        <div className="pq-content">
          {step === 1 && (
            <div className="pq-grid">
              {cutoutOptions.map(opt => (
                <div
                  key={opt.value}
                  className={`pq-card ${data.propertyType === opt.value ? 'selected' : ''}`}
                  onClick={() => select('propertyType', opt.value)}
                >
                  <div className="pq-imgwrap">
                    <img className="pq-img" src={opt.src} alt={opt.label} />
                    <div className="pq-radio" />
                  </div>
                  <h3>{opt.label}</h3>
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="pq-radiolist">
              {['1','2','3','4'].map(v => (
                <label key={v} className="pq-option">
                  <input
                    type="radio"
                    name="bedrooms"
                    checked={data.bedrooms === v}
                    onChange={() => select('bedrooms', v)}
                  />
                  <span>{`${v} Bedroom${v === '1' ? '' : 's'}`}</span>
                </label>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="pq-radiolist">
              {['<50 lack','50 lack-1cr','1cr-2cr','2cr-3cr','>3cr'].map(v => (
                <label key={v} className="pq-option">
                  <input
                    type="radio"
                    name="budget"
                    checked={data.budget === v}
                    onChange={() => select('budget', v)}
                  />
                  <span>{v}</span>
                </label>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="pq-radiolist">
              {[
                ['clubhouse', 'Clubhouse'],
                ['gym', 'Gym & Wellness'],
                ['pool', 'Swimming Pool'],
                ['kids-play', 'Kids Play Area'],
                ['cowork', 'Co-working Space']
              ].map(([v,label]) => (
                <label key={v} className="pq-option">
                  <input
                    type="checkbox"
                    checked={Array.isArray(data.facility) && data.facility.includes(v)}
                    onChange={() => toggleFacility(v)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="pq-radiolist">
              {[
                ['primary-residence','Primary Residence'],
                ['investment','Investment Property'],
                ['holiday-home','Holiday Home'],
                ['resale','Capital Growth & Resale']
              ].map(([v,label]) => (
                <label key={v} className="pq-option">
                  <input
                    type="radio"
                    name="purpose"
                    checked={data.purpose === v}
                    onChange={() => select('purpose', v)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}

          {step === 6 && (
            <form className="pq-form" onSubmit={submit}>
              <label>
                Name
                <input
                  className="pq-input"
                  required
                  value={data.fullName}
                  onChange={(e) => select('fullName', e.target.value)}
                  placeholder="Your name"
                />
              </label>
              <label>
                Email
                <input
                  className="pq-input"
                  type="email"
                  required
                  value={data.email}
                  onChange={(e) => select('email', e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              <label>
                Phone
                <div className="pq-phone">
                  <select
                    className="pq-select"
                    value={data.countryCode}
                    onChange={(e) => select('countryCode', e.target.value)}
                  >
                    {['+971', '+1', '+44', '+91', '+966', '+974'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    className="pq-input"
                    required
                    value={data.phone}
                    onChange={(e) => select('phone', e.target.value)}
                    placeholder="Enter your phone"
                  />
                </div>
              </label>
              <button type="submit" className="pq-submit" disabled={loading}>
                {loading ? 'Sending...' : 'Get My Recommendations'}
              </button>
            </form>
          )}

          {step === 7 && (
            <div className="pq-step active" style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                fontSize: 40,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#2D2D2D',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>✓</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}>Thank You!</h2>
              <p>Our team will contact you within 24 hours with curated listings.</p>
              <button className="pq-btn pq-back" onClick={onClose}>Close</button>
            </div>
          )}
        </div>

        {step !== 7 && (
          <div className="pq-footer">
            <div className="pq-progress">
              <div className="pq-progress-text">Progress {progress}%</div>
              <div className="pq-progress-bar">
                <div className="pq-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="pq-btn pq-back" onClick={back} style={{ display: step > 1 ? 'block' : 'none' }}>←</button>
              <button
                className="pq-btn pq-next"
                onClick={next}
                disabled={!canNext()}
                style={{ display: step === totalSteps ? 'none' : 'block' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="popup-quiz-overlay">
      <div className={`popup-quiz ${step === 1 ? 'pq-step1' : ''}`}>
        {step !== 7 && (
          <div className="pq-header">
            <button className="pq-close" onClick={onClose}>✕</button>
            <h2>
              {step === 1 && 'What type of property are you looking for?'}
              {step === 2 && 'How many bedrooms do you need?'}
              {step === 3 && "What's your budget range?"}
              {step === 4 && 'Which facility do you prefer most?'}
              {step === 5 && "What's your investment purpose?"}
              {step === 6 && 'How can we reach you?'}
            </h2>
          </div>
        )}

        <div className="pq-content">
          {step === 1 && (
            <div className="pq-grid">
              {cutoutOptions.map(opt => (
                <div
                  key={opt.value}
                  className={`pq-card ${data.propertyType === opt.value ? 'selected' : ''}`}
                  onClick={() => select('propertyType', opt.value)}
                >
                  <div className="pq-imgwrap">
                    <img className="pq-img" src={opt.src} alt={opt.label} />
                    <div className="pq-radio" />
                  </div>
                  <h3>{opt.label}</h3>
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="pq-radiolist">
              {['1','2','3','4'].map(v => (
                <label key={v} className="pq-option">
                  <input
                    type="radio"
                    name="bedrooms"
                    checked={data.bedrooms === v}
                    onChange={() => select('bedrooms', v)}
                  />
                  <span>{`${v} Bedroom${v === '1' ? '' : 's'}`}</span>
                </label>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="pq-radiolist">
              {['<50 lack','50 lack-1cr','1cr-2cr','2cr-3cr','>3cr'].map(v => (
                <label key={v} className="pq-option">
                  <input
                    type="radio"
                    name="budget"
                    checked={data.budget === v}
                    onChange={() => select('budget', v)}
                  />
                  <span>{v}</span>
                </label>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="pq-radiolist">
              {[
                ['clubhouse', 'Clubhouse'],
                ['gym', 'Gym & Wellness'],
                ['pool', 'Swimming Pool'],
                ['kids-play', 'Kids Play Area'],
                ['cowork', 'Co-working Space']
              ].map(([v,label]) => (
                <label key={v} className="pq-option">
                  <input
                    type="checkbox"
                    checked={Array.isArray(data.facility) && data.facility.includes(v)}
                    onChange={() => toggleFacility(v)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="pq-radiolist">
              {[
                ['primary-residence','Primary Residence'],
                ['investment','Investment Property'],
                ['holiday-home','Holiday Home'],
                ['resale','Capital Growth & Resale']
              ].map(([v,label]) => (
                <label key={v} className="pq-option">
                  <input
                    type="radio"
                    name="purpose"
                    checked={data.purpose === v}
                    onChange={() => select('purpose', v)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}

          {step === 6 && (
            <form className="pq-form" onSubmit={submit}>
              <label>
                Name
                <input
                  className="pq-input"
                  required
                  value={data.fullName}
                  onChange={(e) => select('fullName', e.target.value)}
                  placeholder="Your name"
                />
              </label>
              <label>
                Email
                <input
                  className="pq-input"
                  type="email"
                  required
                  value={data.email}
                  onChange={(e) => select('email', e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              <label>
                Phone
                <div className="pq-phone">
                  <select
                    className="pq-select"
                    value={data.countryCode}
                    onChange={(e) => select('countryCode', e.target.value)}
                  >
                    {['+971', '+1', '+44', '+91', '+966', '+974'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input
                    className="pq-input"
                    required
                    value={data.phone}
                    onChange={(e) => select('phone', e.target.value)}
                    placeholder="Enter your phone"
                  />
                </div>
              </label>
              <button type="submit" className="pq-submit" disabled={loading}>
                {loading ? 'Sending...' : 'Get My Recommendations'}
              </button>
            </form>
          )}

          {step === 7 && (
            <div className="pq-step active" style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                fontSize: 40,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#2D2D2D',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>✓</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}>Thank You!</h2>
              <p>Our team will contact you within 24 hours with curated listings.</p>
              <button className="pq-btn pq-back" onClick={onClose}>Close</button>
            </div>
          )}
        </div>

        {step !== 7 && (
          <div className="pq-footer">
            <div className="pq-progress">
              <div className="pq-progress-text">Progress {progress}%</div>
              <div className="pq-progress-bar">
                <div className="pq-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="pq-btn pq-back" onClick={back} style={{ display: step > 1 ? 'block' : 'none' }}>←</button>
              <button
                className="pq-btn pq-next"
                onClick={next}
                disabled={!canNext()}
                style={{ display: step === totalSteps ? 'none' : 'block' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
